/**
 * SN_SAI_KNW_REG_001 — 제품 지식 관리 (본사) (2026-09-18, 2차 검토 확정)
 *
 * 왼쪽 지식 목록(sai_know) — 지식 구분 · 이름 · 사용여부. 행을 고르면 오른쪽 폼에 상세(이름 · 한 줄 설명 · 모듈 · 출처 · 본문).
 * 폼에서 고친 값은 gridref 로 그리드 행에 되돌아가고, 툴바 [저장] 이 그리드 변경을 보낸다. 지식 ID 는 서버가 채번한다(숨김).
 * 지식 구분 '답변 규칙'(RULE_BASE)의 본문이 AI 기본 행동 규칙이다 — 다음 질문부터 바로 적용된다.
 * [AI 지식 등록] — 팝업. 문서를 끌어다 놓으면 중앙 AI 가 후보를 추려 쌓고, 여러 개 골라 [적용] 하면 사용여부 N 으로 왼쪽에 들어온다. 폼에서 다듬고 켠다.
 * [도움말] — 지식 구분별 본문 양식과 예시.
 * 저장 때 같은 구분에 비슷한 이름이 있으면 확인창을 띄우고, "그래도 저장" 이면 force 로 다시 보낸다.
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_knw_reg_001_01.
 * 본 : 사업장등록 SN_MAS_ORG_REG_002 (좌 그리드 · 우 SharedForm) · 거래처 SN_MAS_BAS_REG_001_POP_001 (화면 팝업)
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnButton from "@shared/components/SnButton";
import SharedForm from "@widgets/SharedForm";
import { menuInfo } from "@shared/type/CommonType";
import SN_SAI_KNW_REG_001_POP_001 from "./SN_SAI_KNW_REG_001_POP_001";
import SN_SAI_KNW_REG_001_POP_002 from "./SN_SAI_KNW_REG_001_POP_002";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_KNW_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  const sharedFormRef1 = useRef<any>(null);
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
        });
        setSharedForm(rtn.form?.freeform1 || []);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_knw_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      setFocusedRowData(null);
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    const row = gridRefs.current["grid1"].current?.focusedRowNode()?.data;
    if (row?.tp_know === "RULE") {
      await showDialog("alter", "답변 규칙은 지우지 않습니다. 본문을 고치거나 사용여부를 끄세요.");
      return;
    }
    const confirm = await showDialog("confirm", "선택한 지식을 삭제 표시합니다. 저장하면 지워집니다.");
    if (!confirm) return;
    gridRefs.current["grid1"].current.deleteSelectedRows();
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return false;
      const sai_knowChanges = gridRefs.current["grid1"].current.getChange();
      if (sai_knowChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        sai_know: sai_knowChanges,
      };
      const changed = [...(sai_knowChanges.insert || []), ...(sai_knowChanges.update || [])]
        .map((r: any) => ({ no_id_know: r.no_id_know, tp_know: r.tp_know, nm_know: r.nm_know }));
      const dup = await callApi("SN_SAI_KNW_REG_001_btnCheckDup", { rows: changed });
      if (!dup?.status) return false;
      const messages: string[] = Array.isArray(dup.data) ? dup.data : [];
      if (messages.length > 0) {
        const go = await showDialog("confirm", `비슷한 지식이 있습니다.\n\n${messages.join("\n")}\n\n그래도 저장할까요?`);
        if (!go) return false;
        saveData.force = true;
      }
      const ok = await fnSave("SN_SAI_KNW_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 행추가 기본값 — 회사 · 버전 1. 고정값(사용여부 등)은 sys_formgridcolumn.cd_value 가 넣는다 */
  const onBeforeAddKnow = (): any => {
    return { cd_firm: userInfo.cd_firm, no_ver: 1 };
  };
  /** [AI 지식 등록] — 팝업에서 문서 올려 후보 추출 · 고르기 → [적용] 하면 사용여부 N 으로 등록 → 다시 조회 */
  const onRegisterPopup = async () => {
    if (gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0) {
      await showDialog("alter", "수정 중인 내용을 먼저 저장해 주세요.");
      return;
    }
    const result: any = await showPopupModal({
      title: "AI 지식 등록",
      children: <SN_SAI_KNW_REG_001_POP_001 />,
      width: "85%",
      height: "85vh",
      okayBtnText: "적용",
    });
    if (!result || result === "close") return;
    const ids: string[] = Array.isArray(result) ? result.flat().filter(Boolean) : [];
    if (ids.length === 0) return;
    const res = await callApi("SN_SAI_KNW_REG_001_btnRegister", { ids });
    if (!res?.status) return;
    await onSearch();
  };

  /** [도움말] — 지식 구분별 본문 양식 · 예시 */
  const onHelp = async () => {
    await showPopupModal({
      title: "도움말",
      children: <SN_SAI_KNW_REG_001_POP_002 />,
      width: "720px",
      height: "70vh",
      okayBtnText: "닫기",
    });
  };
  /** 왼쪽에서 행을 고르면 오른쪽 폼에 그 행. 폼에서 고친 값은 gridref 로 그리드 행에 되돌아간다 — 저장은 그리드 변경으로 간다 */
  const onFocusedRowChanged = useCallback(
    (row: any | null) => {
      setFocusedRowData(row);
    },
    [focusedRowData]
  );
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddKnow,
          onFocusedRowChanged: onFocusedRowChanged
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panelList" orgSize="23.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "제품 지식", isEditable: false }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panelDetail" orgSize="76.5%">
          <SharedForm
            ref={sharedFormRef1}
            data={focusedRowData}
            controls={sharedForm}
            gridref={gridRefs.current["grid1"]}
            title="상세정보"
            header={
              <div className="flex justify-end">
                <SnButton text="AI 지식 등록" onClick={() => { onRegisterPopup(); }} />
                <SnButton text="도움말" onClick={() => { onHelp(); }} />
              </div>
            }
          ></SharedForm>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SAI_KNW_REG_001;
